import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Modal from 'react-modal';
import Icon from '../../components/AppIcon';
import './Rechercher.css';

// Importation des données des pays
import countriesData from '../../country/countriesData.json';

// Configuration du Modal pour react-modal
Modal.setAppElement('#root');

// Créer une instance axios avec timeout
const api = axios.create({
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Données des packs d'alerte
const ALERT_PACKS = [
  { id: 1, name: 'Pack Bronze', duration: '7 jours', price: 500, features: ['1 alerte active', 'Notifications par email'] },
  { id: 2, name: 'Pack Argent', duration: '30 jours', price: 1500, features: ['5 alertes actives', 'Notifications SMS', 'Priorité haute'] },
  { id: 3, name: 'Pack Or', duration: '90 jours', price: 3500, features: ['Alertes illimitées', 'Notifications push', 'Support prioritaire', 'Statistiques détaillées'] },
];

// Styles personnalisés pour react-modal
const customModalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'hidden',
    padding: '0',
    border: 'none',
    borderRadius: '12px',
    backgroundColor: 'white',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  }
};

const customAlertModalStyles = {
  ...customModalStyles,
  content: {
    ...customModalStyles.content,
    maxWidth: '450px',
  }
};

const customSmallModalStyles = {
  ...customModalStyles,
  content: {
    ...customModalStyles.content,
    maxWidth: '400px',
  }
};

const customSuccessModalStyles = {
  ...customModalStyles,
  content: {
    ...customModalStyles.content,
    maxWidth: '400px',
    textAlign: 'center',
  }
};

const Rechercher = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // États pour la recherche
  const [searchInput, setSearchInput] = useState(location.state?.query || ''); // Saisie en cours
  const [searchQuery, setSearchQuery] = useState(location.state?.query || ''); // Requête active
  
  const [category, setCategory] = useState(location.state?.category || null);
  const [categoryName, setCategoryName] = useState(location.state?.categoryName || null);
  const [subcategoryId, setSubcategoryId] = useState(location.state?.subcategoryId || null);
  const [subcategoryName, setSubcategoryName] = useState(location.state?.subcategoryName || null);
  
  // État pour les résultats
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // État pour la pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  
  // État pour les filtres
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    minPrice: '',
    maxPrice: '',
    etat: '',
    country: null,
    city: ''
  });

  // États pour la sélection du pays et ville
  const [countrySearch, setCountrySearch] = useState('');
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);

  // États pour la localisation
  const [locating, setLocating] = useState(false);
  const [locationPermissionGranted, setLocationPermissionGranted] = useState(false);
  const [showLocationAlert, setShowLocationAlert] = useState(false);

  // États pour la mise en alerte
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  // États pour le paiement Orange Money
  const [paymentData, setPaymentData] = useState(null);
  const [checkStatusInterval, setCheckStatusInterval] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState('');

  // Fonction pour ouvrir la modal d'alerte
  const openAlertModal = () => {
    setShowAlertModal(true);
  };

  // Fonction pour fermer toutes les modales
  const closeAllModals = () => {
    setShowFilters(false);
    setShowAlertModal(false);
    setShowPaymentModal(false);
    setShowPhoneNumberModal(false);
    setShowSuccessModal(false);
    resetPaymentState();
  };

  // Fonction pour réinitialiser l'état du paiement
  const resetPaymentState = () => {
    setPaymentData(null);
    setPaymentStatus(null);
    setPaymentMessage('');
    setSelectedPack(null);
    setSelectedPaymentMethod(null);
    setPhoneNumber('');
    setPaymentLoading(false);
    
    if (checkStatusInterval) {
      clearInterval(checkStatusInterval);
      setCheckStatusInterval(null);
    }
  };

  // Nettoyer l'intervalle quand le composant est démonté
  useEffect(() => {
    return () => {
      if (checkStatusInterval) {
        clearInterval(checkStatusInterval);
      }
    };
  }, [checkStatusInterval]);

  // Fonction pour générer le titre de recherche
  const getSearchTitle = () => {
    if (subcategoryName) {
      return subcategoryName;
    }
    if (categoryName) {
      return categoryName;
    }
    if (searchQuery) {
      return `"${searchQuery}"`;
    }
    return 'Tous les produits';
  };

  // Navigation intelligente en arrière
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate(-1);
    }
  };

  // Fonction pour lancer la recherche manuellement
  const handleSearch = () => {
    if (!searchInput.trim()) {
      alert('Veuillez saisir un terme de recherche');
      return;
    }
    
    // Mettre à jour la vraie requête de recherche
    setSearchQuery(searchInput.trim());
    
    // Réinitialiser les autres critères de recherche
    setCategory(null);
    setCategoryName(null);
    setSubcategoryId(null);
    setSubcategoryName(null);
    
    // Réinitialiser la pagination
    setPage(1);
    setProduits([]);
    setTotalResults(0);
    
    // Lancer la recherche
    fetchResults(true);
  };

  // Fonction pour gérer la touche Entrée
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Charger les résultats au montage
  useEffect(() => {
    // Charger automatiquement seulement si on a déjà une recherche, catégorie ou sous-catégorie
    if (searchQuery || category || subcategoryId) {
      fetchResults(true);
    }
    
    // Demander la permission de localisation au démarrage (pour le web)
    if ('geolocation' in navigator) {
      setLocationPermissionGranted(true);
    }
  }, []);

  // Mettre à jour les états quand location.state change
  useEffect(() => {
    if (location.state) {
      const query = location.state.query || '';
      setSearchInput(query);
      setSearchQuery(query);
      setCategory(location.state.categoryId || null);
      setCategoryName(location.state.categoryName || null);
      setSubcategoryId(location.state.subcategoryId || null);
      setSubcategoryName(location.state.subcategoryName || null);
    }
  }, [location.state]);

  // Charger automatiquement pour les catégories/sous-catégories
  useEffect(() => {
    if (category || subcategoryId) {
      // Réinitialiser la pagination
      setPage(1);
      setProduits([]);
      setTotalResults(0);
      fetchResults(true);
    }
  }, [category, subcategoryId]);

  // Filtrer les pays pour la recherche
  useEffect(() => {
    if (countrySearch.trim() === '') {
      setFilteredCountries(countriesData.slice(0, 195));
    } else {
      const searchLower = countrySearch.toLowerCase();
      const filtered = countriesData.filter(country =>
        country.name.toLowerCase().includes(searchLower) ||
        country.nativeName?.toLowerCase().includes(searchLower) ||
        country.capital?.toLowerCase().includes(searchLower) ||
        country.code.toLowerCase().includes(searchLower)
      ).slice(0, 15);
      setFilteredCountries(filtered);
    }
  }, [countrySearch]);

  // Filtrer les villes pour le pays sélectionné
  useEffect(() => {
    if (filters.country) {
      const countryCities = filters.country.cities || [];
      setFilteredCities(countryCities);
    } else {
      setFilteredCities([]);
    }
  }, [filters.country]);

  // Fonction pour gérer les erreurs de recherche
  const handleSearchError = (error, reset) => {
    let errorMessage = 'Impossible de charger les résultats de recherche';
    
    if (error.code === 'ECONNABORTED') {
      errorMessage = 'Temps d\'attente dépassé. Vérifiez votre connexion.';
    } else if (error.response) {
      console.error('Response error:', error.response.status, error.response.data);
      
      // Messages d'erreur spécifiques
      switch (error.response.status) {
        case 400:
          errorMessage = 'Requête invalide. Vérifiez vos paramètres de recherche.';
          break;
        case 404:
          if (subcategoryId) {
            errorMessage = `La sous-catégorie "${subcategoryName}" n'existe pas ou ne contient aucun produit.`;
          } else if (category) {
            errorMessage = `La catégorie "${categoryName}" n'existe pas ou ne contient aucun produit.`;
          } else {
            errorMessage = 'Aucun résultat trouvé pour cette recherche.';
          }
          break;
        case 500:
          errorMessage = 'Erreur serveur. Veuillez réessayer plus tard.';
          break;
        default:
          errorMessage = `Erreur serveur (${error.response.status})`;
      }
    } else if (error.request) {
      errorMessage = 'Pas de connexion au serveur. Vérifiez votre réseau.';
    }
    
    setError(errorMessage);
    
    // Afficher une alerte seulement pour les erreurs non-404
    if (!errorMessage.includes('404') && !errorMessage.includes('aucun produit')) {
      alert(`Erreur: ${errorMessage}`);
    }
    
    if (reset) {
      setProduits([]);
      setTotalResults(0);
    }
  };

  // Fonction pour rechercher les produits
  const fetchResults = async (reset = false) => {
    try {
      if (reset) {
        setPage(1);
        setLoading(true);
      } else {
        if (!hasMore || loading) return;
      }
      
      setError(null);
      
      let url = '';
      let params = new URLSearchParams();
      
      // Paramètres communs
      params.append('page', reset ? 1 : page);
      params.append('limit', 20);
      
      // Ajouter les filtres
      if (filters.minPrice) params.append('minPrice', filters.minPrice);
      if (filters.maxPrice) params.append('maxPrice', filters.maxPrice);
      if (filters.etat) params.append('etat', filters.etat);
      if (filters.country) params.append('country', filters.country.name);
      if (filters.city) params.append('city', filters.city);
      
      // Logique de sélection d'endpoint selon votre spécification
      if (subcategoryId) {
        // Endpoint spécifique pour sous-catégorie
        url = `https://api.autocasses.com/api/produit/sous-categorie/${subcategoryId}?${params.toString()}`;
        console.log('📱 Endpoint Sous-catégorie:', subcategoryId, 'Nom:', subcategoryName);
      } else if (category || searchQuery) {
        // Endpoint search pour catégories ou recherche texte
        if (searchQuery) params.append('q', searchQuery);
      
        if (category) {
            url = `https://api.autocasses.com/api/produit/categories/${category}?${params.toString()}`;
          console.log('📁 Endpoint Catégorie:', category, 'Nom:', categoryName);
        } else if (searchQuery) {
          url = `https://api.autocasses.com/api/produit/search?${params.toString()}`;
          console.log('🔍 Endpoint Recherche texte:', searchQuery);
        }
      } else {
        // Endpoint search général (tous les produits)
        url = `https://api.autocasses.com/api/produit/search?${params.toString()}`;
        console.log('🌐 Endpoint Général (tous les produits)');
      }
      
      console.log('🌐 URL complète:', url);
      console.log('🔍 Paramètres de recherche:', {
        searchQuery,
        category,
        subcategoryId,
        categoryName,
        subcategoryName,
        filters
      });
      
      const response = await api.get(url);
      const data = response.data;
      
      console.log('✅ Réponse API:', {
        total: data.total,
        produits: data.produits?.length || 0,
        page: data.page,
        totalPages: data.totalPages
      });
      
      if (reset) {
        setProduits(data.produits || []);
      } else {
        setProduits(prev => [...prev, ...(data.produits || [])]);
      }
      
      setTotalResults(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setHasMore(data.page < data.totalPages);
      
      if (!reset && data.page) {
        setPage(data.page + 1);
      } else if (!reset) {
        setPage(prev => prev + 1);
      }
      
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      handleSearchError(error, reset);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Fonction pour obtenir la localisation actuelle
  const getCurrentLocation = async () => {
    setLocating(true);
    
    try {
      if (!('geolocation' in navigator)) {
        alert('La géolocalisation n\'est pas supportée par votre navigateur');
        setLocating(false);
        return;
      }

      console.log('Début de la géolocalisation...');
      
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          console.log('Position obtenue:', latitude, longitude);
          reverseGeocode(latitude, longitude);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
          setLocating(false);
          
          let errorMessage = 'Impossible d\'obtenir votre localisation';
          switch (error.code) {
            case 1:
              errorMessage = 'Permission de localisation refusée';
              break;
            case 2:
              errorMessage = 'Position indisponible';
              break;
            case 3:
              errorMessage = 'Temps d\'attente dépassé';
              break;
          }
          
          alert(`Erreur de localisation: ${errorMessage}`);
        },
        { 
          enableHighAccuracy: true, 
          timeout: 15000, 
          maximumAge: 10000 
        }
      );
    } catch (error) {
      console.error('Erreur lors de la localisation:', error);
      setLocating(false);
      alert('Erreur: Une erreur est survenue lors de la localisation');
    }
  };

  // Traitement automatique de la localisation
  const handleAutoLocation = () => {
    setShowLocationAlert(true);
    setTimeout(() => {
      getCurrentLocation();
    }, 100);
  };

  // Géocodage inversé pour obtenir la ville et le pays
  const reverseGeocode = async (latitude, longitude) => {
    try {
      console.log('Début du géocodage inversé...');
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=10&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'AutoCassesApp/1.0'
          }
        }
      );

      const data = response.data;
      const address = data.address;
      
      let city = '';
      let countryName = '';
      
      if (address.city) {
        city = address.city;
      } else if (address.town) {
        city = address.town;
      } else if (address.village) {
        city = address.village;
      } else if (address.municipality) {
        city = address.municipality;
      }
      
      if (address.country) {
        countryName = address.country;
      }
      
      console.log('Adresse trouvée:', { city, countryName, fullAddress: address });
      
      const foundCountry = countriesData.find(country => 
        country.name.toLowerCase().includes(countryName.toLowerCase()) ||
        country.nativeName?.toLowerCase().includes(countryName.toLowerCase()) ||
        (address.country_code && country.code.toLowerCase() === address.country_code.toLowerCase())
      );
      
      if (foundCountry) {
        setFilters(prev => ({
          ...prev,
          country: foundCountry,
          city: city
        }));
        
        // Appliquer automatiquement les filtres après 1 seconde
        setTimeout(() => {
          setShowFilters(false);
          fetchResults(true);
        }, 1000);
        
        alert(`✅ Localisation réussie\nPosition détectée: ${city ? city + ', ' : ''}${foundCountry.name}\n\nRecherche en cours...`);
      } else {
        const confirmUse = confirm(
          `ℹ️ Localisation partielle\nPosition trouvée${city ? ' à ' + city : ''}${countryName ? ' en ' + countryName : ''}\n\nVoulez-vous utiliser cette localisation?`
        );
        
        if (confirmUse) {
          const tempCountry = {
            name: countryName || 'Localisation',
            code: 'custom',
            flag: '📍',
            cities: city ? [city] : []
          };
          
          setFilters(prev => ({
            ...prev,
            country: tempCountry,
            city: city
          }));
          
          setTimeout(() => {
            setShowFilters(false);
            fetchResults(true);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Erreur de géocodage inversé:', error);
      alert('❌ Géocodage échoué\nImpossible de déterminer votre ville et pays. Vérifiez votre connexion internet.');
    } finally {
      setLocating(false);
      setShowLocationAlert(false);
    }
  };

  // Formater le prix des alertes
  const formatAlertPrice = (price) => {
    return `${price} FCFA`;
  };

  // Valider le numéro de téléphone
  const validatePhoneNumber = (number, operator) => {
    const cleaned = number.replace(/\s/g, '');
    
    if (operator === 'orange') {
      const orangeRegex = /^(6|7|9)[0-9]{8}$/;
      return orangeRegex.test(cleaned);
    } else if (operator === 'mtn') {
      const mtnRegex = /^(6|7|9)[0-9]{8}$/;
      return mtnRegex.test(cleaned);
    }
    
    return false;
  };

  // Fonction pour gérer le paiement
  const handlePayment = async () => {
    if (selectedPaymentMethod === 'inapp') {
      // Logique existante pour paiement in-app
      setPaymentLoading(true);
      
      try {
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        if (selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn') {
          if (!validatePhoneNumber(phoneNumber, selectedPaymentMethod)) {
            alert('Erreur', `Numéro ${selectedPaymentMethod === 'orange' ? 'Orange Money' : 'MTN Money'} invalide`);
            setPaymentLoading(false);
            return;
          }
        }
        
        setShowPhoneNumberModal(false);
        setShowPaymentModal(false);
        
        setTimeout(() => {
          setShowSuccessModal(true);
          
          const alertData = {
            query: searchQuery,
            categoryId: category,
            subcategoryId: subcategoryId,
            filters: filters,
            packId: selectedPack.id,
            paymentMethod: selectedPaymentMethod,
            phoneNumber: selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn' ? phoneNumber : null,
            createdAt: new Date().toISOString()
          };
          
          console.log('Alerte créée:', alertData);
          
          setTimeout(() => {
            setShowSuccessModal(false);
            setSelectedPack(null);
            setSelectedPaymentMethod(null);
            setPhoneNumber('');
          }, 3000);
        }, 500);
        
      } catch (error) {
        alert('Erreur', 'Le paiement a échoué. Veuillez réessayer.');
      } finally {
        setPaymentLoading(false);
      }
    } else if (selectedPaymentMethod === 'orange') {
      // Paiement Orange Money
      await handleOrangeMoneyPayment();
    }
  };

  // Fonction pour gérer le paiement Orange Money
  const handleOrangeMoneyPayment = async () => {
          const token = localStorage.getItem('userToken');
    if (!validatePhoneNumber(phoneNumber, 'orange')) {
      alert('Erreur', 'Numéro Orange Money invalide. Format: 6XX XXX XXX, 7XX XXX XXX ou 9XX XXX XXX');
      setPaymentLoading(false);
      return;
    }

    setPaymentLoading(true);
    setPaymentStatus('INITIATING');
    setPaymentMessage('Initialisation du paiement...');
    
    try {
      // Étape 1: Initier le paiement
      const paymentResponse = await axios.post(
        'https://api.autocasses.com/api/orange/orange-money/initiate',
        {
          montant: selectedPack.price,
          numero: phoneNumber,
          motif: `paiement du pack ${selectedPack.name}`,
          packId: selectedPack.id
        },
        {
          headers: {
            'Content-Type': 'application/json',
             'Authorization': `Bearer ${token}`
          },
          timeout: 30000
        }
      );

      console.log('Réponse paiement:', paymentResponse.data);
      
      if (paymentResponse.data && paymentResponse.data[0]) {
        const paymentData = paymentResponse.data[0];
        setPaymentData(paymentData);
        setPaymentStatus('PENDING');
        setPaymentMessage(paymentData.inittxnmessage || 'Veuillez confirmer le paiement dans votre application Orange Money');
        
        // Afficher le message à l'utilisateur
        alert('Paiement initié', paymentData.inittxnmessage || 'Veuillez confirmer le paiement dans votre application Orange Money');
        
        // Démarrer la vérification du statut
        startCheckingStatus(paymentData.payToken, paymentData.Token);
        
        // Ne pas fermer la modale pour voir l'état du paiement
      } else {
        throw new Error('Réponse de paiement invalide');
      }
      
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      setPaymentStatus('FAILED');
      setPaymentMessage('Échec de l\'initiation du paiement. Veuillez réessayer.');
      alert('Erreur', 'Échec de l\'initiation du paiement. Veuillez réessayer.');
      setPaymentLoading(false);
    }
  };

  // Fonction pour démarrer la vérification du statut
  const startCheckingStatus =async (payToken, token) => {
              const tokenApp = localStorage.getItem('userToken');
    // Arrêter tout intervalle existant
    if (checkStatusInterval) {
      clearInterval(checkStatusInterval);
    }
    
    // Démarrer un nouvel intervalle de vérification
    const interval = setInterval(async () => {
      try {
        const statusResponse = await axios({
          method: 'get',
          url: 'https://api.autocasses.com/api/orange/orange-money/check-status',
          data: {
            payToken: payToken,
            Token: token
          },
          headers: {
            'Content-Type': 'application/json',
             'Authorization': `Bearer ${tokenApp}`
          },
          timeout: 10000
        });

        console.log('Statut du paiement:', statusResponse.data);
        
        if (statusResponse.data && statusResponse.data.data) {
          const transaction = statusResponse.data.data;
          setPaymentStatus(transaction.status);
          
          if (transaction.status === 'SUCCESS') {
            // Paiement réussi
            setPaymentMessage('Paiement confirmé avec succès !');
            handleSuccessfulPayment(transaction);
            clearInterval(interval);
            setCheckStatusInterval(null);
          } else if (transaction.status === 'FAILED' || transaction.status === 'CANCELED') {
            // Paiement échoué ou annulé
            setPaymentMessage('Paiement échoué ou annulé');
            handleFailedPayment(transaction);
            clearInterval(interval);
            setCheckStatusInterval(null);
          } else {
            setPaymentMessage('En attente de confirmation...');
          }
        }
      } catch (error) {
        console.error('Erreur vérification statut:', error);
        // On continue à essayer en cas d'erreur temporaire
      }
    }, 5000); // Vérifier toutes les 5 secondes
    
    setCheckStatusInterval(interval);
    
    // Arrêter après 5 minutes (300 secondes)
    setTimeout(() => {
      if (checkStatusInterval) {
        clearInterval(interval);
        setCheckStatusInterval(null);
        if (paymentStatus !== 'SUCCESS') {
          setPaymentStatus('TIMEOUT');
          setPaymentMessage('Le paiement a expiré. Veuillez réessayer.');
          alert('Délai dépassé', 'Le paiement a expiré. Veuillez réessayer.');
        }
      }
    }, 300000);
  };

  // Fonction pour calculer la date d'expiration
  const calculateExpirationDate = (duration) => {
    const now = new Date();
    switch (duration) {
      case '7 jours':
        now.setDate(now.getDate() + 7);
        break;
      case '30 jours':
        now.setDate(now.getDate() + 30);
        break;
      case '90 jours':
        now.setDate(now.getDate() + 90);
        break;
      default:
        now.setDate(now.getDate() + 7);
    }
    return now.toISOString();
  };

  // Fonction pour gérer un paiement réussi
  const handleSuccessfulPayment = (transaction) => {
    // Enregistrer l'alerte dans votre base de données
    const alertData = {
      query: searchQuery,
      categoryId: category,
      subcategoryId: subcategoryId,
      filters: filters,
      packId: selectedPack.id,
      packName: selectedPack.name,
      packDuration: selectedPack.duration,
      paymentMethod: 'orange',
      phoneNumber: phoneNumber,
      transactionId: transaction.txnid,
      payToken: transaction.payToken,
      amount: transaction.amount,
      status: 'active',
      createdAt: new Date().toISOString(),
      expiresAt: calculateExpirationDate(selectedPack.duration)
    };
    
    console.log('Paiement réussi, alerte créée:', alertData);
    
    // ICI: Appelez votre API pour enregistrer l'alerte dans la base de données
    // Exemple: 
    // try {
    //   const saveResponse = await axios.post(
    //     'http://192.168.145.133:3001/api/alerts/create',
    //     alertData
    //   );
    //   console.log('Alerte enregistrée:', saveResponse.data);
    // } catch (error) {
    //   console.error('Erreur enregistrement alerte:', error);
    // }
    
    // Fermer la modale de téléphone après 2 secondes
    setTimeout(() => {
      setShowPhoneNumberModal(false);
      setTimeout(() => {
        setShowSuccessModal(true);
      }, 50);
    }, 2000);
  };

  // Fonction pour gérer un paiement échoué
  const handleFailedPayment = (transaction) => {
    setTimeout(() => {
      alert('Paiement échoué', 'Le paiement a été annulé ou a échoué. Veuillez réessayer.');
      setShowPhoneNumberModal(false);
      resetPaymentState();
    }, 1000);
  };

  // Rafraîchir les résultats
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    fetchResults(true);
  }, [searchQuery, category, subcategoryId, filters]);

  // Charger plus de résultats
  const loadMore = () => {
    if (!loading && hasMore) {
      fetchResults(false);
    }
  };

  // Appliquer les filtres
  const applyFilters = () => {
    setShowFilters(false);
    fetchResults(true);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setFilters({
      minPrice: '',
      maxPrice: '',
      etat: '',
      country: null,
      city: ''
    });
    setCountrySearch('');
    fetchResults(true);
  };

  // Sélectionner un pays
  const handleSelectCountry = (country) => {
    setFilters(prev => ({ 
      ...prev, 
      country: country,
      city: ''
    }));
    setCountrySearch('');
  };

  // Sélectionner une ville
  const handleSelectCity = (city) => {
    setFilters(prev => ({ ...prev, city }));
  };

  // Formater le prix des produits
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Naviguer vers les détails du produit
  const navigateToProduct = (produit) => {
     navigate('/product', { 
      state: { 
        productId: produit.id,
        productData: produit
      }
    });
  };

  // Rendu d'un produit dans la grille
  const renderProductItem = (item, index) => (
    <div
      key={item.id}
      className="product-card"
      onClick={() => navigateToProduct(item)}
    >
      <div className="product-image-container">
        {item.photos && item.photos.length > 0 ? (
          <img
            src={item.photos[0].photo_url}
            alt={item.titre}
            className="product-image"
          />
        ) : (
          <div className="product-image no-image">
            <Icon name="Image" size={30} color="#94a3b8" />
          </div>
        )}
        
        {item.etat && (
          <div className={`etat-badge ${item.etat.toLowerCase().replace(' ', '-')}`}>
            <span className="etat-text">{item.etat}</span>
          </div>
        )}
        
        {item.negociable === 'true' && (
          <div className="negociable-badge">
            <Icon name="DollarSign" size={10} color="#fff" />
            <span className="negociable-text">Nég.</span>
          </div>
        )}
      </div>
      
      <div className="product-info">
        <h3 className="product-title" title={item.titre}>
          {item.titre}
        </h3>
        
        <p className="product-price">
          {formatPrice(item.prix)}
        </p>
        
        <div className="product-meta">
          <div className="location-container">
            <Icon name="MapPin" size={12} color="#64748b" />
            <span className="product-location" title={`${item.city || 'Ville'}, ${item.country || 'Pays'}`}>
              {item.city || 'Ville'}, {item.country || 'Pays'}
            </span>
          </div>
          
          <span className="product-date">
            {new Date(item.createdAt).toLocaleDateString('fr-FR', {
              day: '2-digit',
              month: 'short'
            })}
          </span>
        </div>
      </div>
    </div>
  );

  // Rendu du footer
  const renderFooter = () => {
    if (!loading && !hasMore && produits.length > 0) {
      return (
        <div className="footer">
          <span className="footer-text">🎯 Fin des résultats</span>
        </div>
      );
    }
    
    if (loading && page > 1) {
      return (
        <div className="footer">
          <div className="loader"></div>
          <span className="footer-text">Chargement...</span>
        </div>
      );
    }
    
    return null;
  };

  // Rendu vide
  const renderEmpty = () => {
    if (loading) return null;
    
    let title = 'Aucun résultat trouvé';
    let message = '';
    let showAlertButton = true;
    
    if (error && error.includes('404')) {
      // Cas spécifique: sous-catégorie ou catégorie vide
      if (subcategoryName) {
        title = `Aucun produit dans "${subcategoryName}"`;
        message = `La sous-catégorie "${subcategoryName}" ne contient aucun produit pour le moment.`;
      } else if (categoryName) {
        title = `Aucun produit dans "${categoryName}"`;
        message = `La catégorie "${categoryName}" ne contient aucun produit pour le moment.`;
      }
    } else if (searchQuery) {
      title = `Aucun résultat pour "${searchQuery}"`;
      message = 'Essayez avec d\'autres termes de recherche ou modifiez vos filtres.';
    } else if (subcategoryName) {
      title = `Aucun produit dans "${subcategoryName}"`;
      message = 'Cette sous-catégorie ne contient aucun produit pour le moment.';
    } else if (categoryName) {
      title = `Aucun produit dans "${categoryName}"`;
      message = 'Cette catégorie ne contient aucun produit pour le moment.';
    } else {
      title = 'Aucun produit disponible';
      message = 'Aucun produit n\'est disponible pour le moment.';
      showAlertButton = false;
    }
    
    return (
      <div className="empty-container">
        <div className="empty-illustration">
          <Icon name="Search" size={80} color="#cbd5e1" />
        </div>
        <h2 className="empty-title">{title}</h2>
        {message && (
          <p className="empty-text">
            {message}
          </p>
        )}
        
        <div className="empty-actions">
          <button
            className="empty-button modify-button"
            onClick={() => navigate(-1)}
          >
            <Icon name="Search" size={18} color="#fff" />
            <span className="empty-button-text">Modifier la recherche</span>
          </button>
          
          {showAlertButton && (
            <button
              className="empty-button alert-button"
              onClick={openAlertModal}
            >
              <Icon name="Bell" size={18} color="#fff" />
              <span className="empty-button-text">Mettre en alerte</span>
            </button>
          )}
        </div>
        
        {showAlertButton && (
          <p className="alert-info">
            🔔 Activez une alerte pour être notifié dès qu'un produit correspondant est disponible.
          </p>
        )}
      </div>
    );
  };

  // Gérer le scroll infini
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      loadMore();
    }
  };

  return (
    <div className="rechercher-container">
      {/* En-tête amélioré */}
      <header className="header">
        <button 
          className="back-button"
          onClick={handleBack}
        >
          <Icon name="ArrowLeft" size={22} color="#1e293b" />
        </button>
        
        <div className="search-container">
          <div className="search-bar">
            <Icon name="Search" size={20} color="#64748b" />
            <input
              type="text"
              className="search-input"
              placeholder="Rechercher un produit..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleKeyPress}
            />
            {searchInput.length > 0 && (
              <>
                <button 
                  className="search-button"
                  onClick={handleSearch}
                >
                  <Icon name="Search" size={18} color="#3b82f6" />
                </button>
                <button 
                  className="clear-button"
                  onClick={() => setSearchInput('')}
                >
                  <Icon name="X" size={18} color="#94a3b8" />
                </button>
              </>
            )}
          </div>
          
          <button 
            className={`filter-button ${Object.values(filters).some(f => f) ? 'active' : ''}`}
            onClick={() => setShowFilters(true)}
          >
            <Icon 
              name="Filter" 
              size={22} 
              color={Object.values(filters).some(f => f) ? '#fff' : '#3b82f6'} 
            />
            {Object.values(filters).some(f => f) && (
              <span className="filter-badge">
                {Object.values(filters).filter(f => f).length}
              </span>
            )}
          </button>
        </div>
      </header>
      
      {/* Barre d'information */}
      <div className="info-bar">
        <div className="info-left">
          <h1 className="search-title" title={getSearchTitle()}>
            {getSearchTitle()}
          </h1>
          {totalResults > 0 && (
            <p className="results-count">
              {totalResults} {totalResults === 1 ? 'produit' : 'produits'}
            </p>
          )}
        </div>
        
        {produits.length > 0 && (
          <button 
            className="sort-button"
            onClick={() => setShowFilters(true)}
          >
            <Icon name="SlidersHorizontal" size={18} color="#3b82f6" />
            <span className="sort-text">Filtrer</span>
          </button>
        )}
      </div>
      
      {/* Filtres actifs */}
      {(filters.country || filters.city || filters.etat || filters.minPrice || filters.maxPrice) && (
        <div className="active-filters-container">
          <div className="active-filters-scroll">
            {filters.country && (
              <div className="active-filter-tag">
                <Icon name="Flag" size={12} color="#fff" />
                <span className="active-filter-text">{filters.country.name}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, country: null, city: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.city && (
              <div className="active-filter-tag">
                <Icon name="Building" size={12} color="#fff" />
                <span className="active-filter-text">{filters.city}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, city: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.etat && (
              <div className="active-filter-tag">
                <Icon name="CheckCircle" size={12} color="#fff" />
                <span className="active-filter-text">{filters.etat}</span>
                <button onClick={() => setFilters(prev => ({ ...prev, etat: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.minPrice && (
              <div className="active-filter-tag">
                <Icon name="TrendingUp" size={12} color="#fff" />
                <span className="active-filter-text">Min: {filters.minPrice} FCFA</span>
                <button onClick={() => setFilters(prev => ({ ...prev, minPrice: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            {filters.maxPrice && (
              <div className="active-filter-tag">
                <Icon name="TrendingDown" size={12} color="#fff" />
                <span className="active-filter-text">Max: {filters.maxPrice} FCFA</span>
                <button onClick={() => setFilters(prev => ({ ...prev, maxPrice: '' }))}>
                  <Icon name="X" size={12} color="#fff" />
                </button>
              </div>
            )}
            <button 
              className="clear-all-button"
              onClick={resetFilters}
            >
              <span className="clear-all-text">Tout effacer</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Grille des résultats */}
      <div className="grid-container" onScroll={handleScroll}>
        <div className="products-grid">
          {produits.map((item, index) => renderProductItem(item, index))}
        </div>
        {renderFooter()}
        {produits.length === 0 && !loading && renderEmpty()}
      </div>
      
      {/* Bouton flottant de recherche */}
      {(searchQuery || category || subcategoryId) && produits.length > 0 && (
        <button
          className="floating-button"
          onClick={() => fetchResults(true)}
        >
          <div className="floating-button-content">
            <Icon name="Search" size={22} color="#fff" />
            <span className="floating-button-text">
              {getSearchTitle()}
            </span>
          </div>
        </button>
      )}
      
      {/* Modal des filtres */}
      <Modal
        isOpen={showFilters}
        onRequestClose={() => setShowFilters(false)}
        style={customModalStyles}
        contentLabel="Filtres"
      >
        <div className="modal-content-inner">
          <div className="modal-header">
            <div className="modal-header-left">
              <button 
                className="modal-close-button"
                onClick={() => setShowFilters(false)}
              >
                <Icon name="X" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">Filtres</h2>
            </div>
            
            <button 
              className="modal-reset-button"
              onClick={resetFilters}
            >
              <span className="modal-reset-text">Réinitialiser</span>
            </button>
          </div>
          
          <div className="filters-container">
            {/* Section Localisation */}
            <div className="filter-section">
              <h3 className="section-title">📍 Localisation</h3>
              
              <button
                className={`location-button ${locating ? 'locating' : ''}`}
                onClick={handleAutoLocation}
                disabled={locating}
              >
                <div className="location-button-icon">
                  {locating ? (
                    <div className="loader"></div>
                  ) : (
                    <Icon name="Navigation" size={22} color="#fff" />
                  )}
                </div>
                <div className="location-button-text-container">
                  <span className="location-button-title">
                    {locating ? 'Localisation en cours...' : 'Me localiser'}
                  </span>
                  <span className="location-button-subtitle">
                    Utiliser ma position actuelle
                  </span>
                </div>
                <Icon name="ChevronRight" size={20} color="#fff" />
              </button>
              
              {showLocationAlert && (
                <div className="location-alert">
                  <div className="loader"></div>
                  <span className="location-alert-text">
                    Recherche de votre position...
                  </span>
                </div>
              )}
            </div>
            
            {/* Section Prix */}
            <div className="filter-section">
              <h3 className="section-title">💰 Prix</h3>
              <div className="price-row">
                <div className="price-input-container">
                  <label className="price-label">Min (FCFA)</label>
                  <input
                    type="number"
                    className="price-input"
                    placeholder="0"
                    value={filters.minPrice}
                    onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                  />
                </div>
                <div className="price-separator">
                  <span className="price-separator-text">à</span>
                </div>
                <div className="price-input-container">
                  <label className="price-label">Max (FCFA)</label>
                    <input
                      type="number"
                      className="price-input"
                      placeholder="Illimité"
                      value={filters.maxPrice}
                      onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
              
              {/* Section État */}
              <div className="filter-section">
                <h3 className="section-title">⚡ État</h3>
                <div className="chip-container">
                  {['', 'Neuf', 'Bon État', 'État Correct', 'Mauvais État','Comme neuf', 'Pour Pièces Uniquement'].map((etat) => (
                    <button
                      key={etat}
                      className={`chip ${filters.etat === etat ? 'selected' : ''}`}
                      onClick={() => setFilters(prev => ({ ...prev, etat }))}
                    >
                      <span className={`chip-text ${filters.etat === etat ? 'selected' : ''}`}>
                        {etat || 'Tous états'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Section Pays */}
              <div className="filter-section" style={{marginBottom: '30px'}}>
                <h3 className="section-title">🌍 Pays</h3>
                <div className="country-search-container">
                  <Icon name="Search" size={18} color="#64748b" />
                  <input
                    type="text"
                    className="country-search-input"
                    placeholder="Rechercher un pays..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                  />
                </div>
                
                <div className="country-list">
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      className={`country-item ${filters.country?.code === country.code ? 'selected' : ''}`}
                      onClick={() => handleSelectCountry(country)}
                    >
                      <span className="country-flag">{country.flag}</span>
                      <div className="country-info">
                        <span className="country-name">{country.name}</span>
                        <span className="country-code">{country.code}</span>
                      </div>
                      {filters.country?.code === country.code && (
                        <Icon name="CheckCircle" size={20} color="#10b981" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Section Ville */}
              {filters.country && filteredCities.length > 0 && (
                <div className="filter-section" style={{marginBottom: '150px'}}>
                  <h3 className="section-title">🏙️ Ville</h3>
                  <div className="city-list">
                    {filteredCities.map((city, index) => (
                      <button
                        key={index}
                        className={`city-item ${filters.city === city ? 'selected' : ''}`}
                        onClick={() => handleSelectCity(city)}
                      >
                        <Icon 
                          name="Building" 
                          size={18} 
                          color={filters.city === city ? '#3b82f6' : '#64748b'} 
                        />
                        <span className={`city-text ${filters.city === city ? 'selected' : ''}`}>
                          {city}
                        </span>
                        {filters.city === city && (
                          <Icon name="CheckCircle" size={18} color="#10b981" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Bouton Appliquer */}
            <div className="modal-footer">
              <button
                className="apply-button"
                onClick={applyFilters}
              >
                <span className="apply-button-text">
                  Appliquer les filtres
                  {totalResults > 0 && ` (${totalResults})`}
                </span>
              </button>
            </div>
          </div>
        </Modal>
        
        {/* Modal des packs d'alerte */}
        <Modal
          isOpen={showAlertModal}
          onRequestClose={() => setShowAlertModal(false)}
          style={customAlertModalStyles}
          contentLabel="Packs d'alerte"
        >
          <div className="modal-content-inner">
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => setShowAlertModal(false)}
              >
                <Icon name="X" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">Choisissez un pack d'alerte</h2>
            </div>
            
            <div className="alert-packs-container">
              <p className="alert-modal-description">
                Soyez notifié dès qu'un produit correspondant à votre recherche est disponible
              </p>
              
              {ALERT_PACKS.map((pack) => (
                <div
                  key={pack.id}
                  className={`alert-pack-card ${selectedPack?.id === pack.id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPack(pack);
                    setTimeout(() => {
                      setShowAlertModal(false);
                      setTimeout(() => {
                        setShowPaymentModal(true);
                      }, 50);
                    }, 50);
                  }}
                >
                  <div className="alert-pack-header">
                    <h3 className="alert-pack-name">{pack.name}</h3>
                    <span className="alert-pack-duration">{pack.duration}</span>
                  </div>
                  
                  <p className="alert-pack-price">
                    {formatAlertPrice(pack.price)}
                  </p>
                  
                  <div className="alert-pack-features">
                    {pack.features.map((feature, index) => (
                      <div key={index} className="alert-pack-feature">
                        <Icon name="CheckCircle" size={16} color="#10b981" />
                        <span className="alert-pack-feature-text">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button
                    className="alert-pack-select-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedPack(pack);
                      setTimeout(() => {
                        setShowAlertModal(false);
                        setTimeout(() => {
                          setShowPaymentModal(true);
                        }, 50);
                      }, 50);
                    }}
                  >
                    <span className="alert-pack-select-button-text">
                      Sélectionner
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Modal>
        
        {/* Modal de paiement */}
        <Modal
          isOpen={showPaymentModal}
          onRequestClose={() => {
            setShowPaymentModal(false);
            setSelectedPaymentMethod(null);
          }}
          style={customSmallModalStyles}
          contentLabel="Méthode de paiement"
        >
          <div className="modal-content-inner">
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPaymentMethod(null);
                }}
              >
                <Icon name="ArrowLeft" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">Méthode de paiement</h2>
            </div>
            
            {selectedPack && (
              <div className="payment-summary">
                <p className="payment-summary-title">Pack {selectedPack.name}</p>
                <p className="payment-summary-price">{formatAlertPrice(selectedPack.price)}</p>
              </div>
            )}
            
            <div className="payment-methods-container">
              <button
                className={`payment-method-card ${selectedPaymentMethod === 'orange' ? 'selected' : ''}`}
                onClick={() => setSelectedPaymentMethod('orange')}
              >
                <div className="payment-method-icon">
                  <span className="payment-method-icon-text">🟠</span>
                </div>
                <div className="payment-method-info">
                  <h4 className="payment-method-name">Orange Money</h4>
                  <p className="payment-method-description">Paiement sécurisé via votre compte Orange Money</p>
                </div>
                {selectedPaymentMethod === 'orange' && (
                  <Icon name="CheckCircle" size={24} color="#10b981" />
                )}
              </button>
              
              <button
                className={`payment-method-card ${selectedPaymentMethod === 'mtn' ? 'selected' : ''}`}
                onClick={() => setSelectedPaymentMethod('mtn')}
              >
                <div className="payment-method-icon">
                  <span className="payment-method-icon-text">🟡</span>
                </div>
                <div className="payment-method-info">
                  <h4 className="payment-method-name">MTN Money</h4>
                  <p className="payment-method-description">Paiement sécurisé via votre compte MTN Money</p>
                </div>
                {selectedPaymentMethod === 'mtn' && (
                  <Icon name="CheckCircle" size={24} color="#10b981" />
                )}
              </button>
            </div>
            
            {(selectedPaymentMethod === 'orange' || selectedPaymentMethod === 'mtn') && (
              <button
                className="payment-proceed-button"
                onClick={() => {
                  setShowPaymentModal(false);
                  setTimeout(() => {
                    setShowPhoneNumberModal(true);
                  }, 50);
                }}
                disabled={paymentLoading}
              >
                <span className="payment-proceed-button-text">
                  {paymentLoading ? 'Chargement...' : 'Continuer'}
                </span>
              </button>
            )}
          </div>
        </Modal>
        
        {/* Modal pour numéro de téléphone */}
        <Modal
          isOpen={showPhoneNumberModal}
          onRequestClose={() => {
            setShowPhoneNumberModal(false);
            setPhoneNumber('');
            setShowPaymentModal(true);
          }}
          style={customSmallModalStyles}
          contentLabel="Numéro de téléphone"
        >
          <div className="modal-content-inner">
            <div className="modal-header">
              <button 
                className="modal-close-button"
                onClick={() => {
                  setShowPhoneNumberModal(false);
                  setPhoneNumber('');
                  setShowPaymentModal(true);
                }}
              >
                <Icon name="ArrowLeft" size={24} color="#64748b" />
              </button>
              <h2 className="modal-title">
                {selectedPaymentMethod === 'orange' ? 'Orange Money' : 'MTN Money'}
              </h2>
            </div>
            
            <div className="phone-modal-body">
              <p className="phone-modal-description">
                Entrez votre numéro de téléphone pour procéder au paiement de {selectedPack ? formatAlertPrice(selectedPack.price) : ''}
              </p>
              
              <div className="phone-input-container">
                <span className="phone-prefix">+237</span>
                <input
                  type="tel"
                  className="phone-input"
                  placeholder="6XX XXX XXX"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  maxLength={9}
                />
              </div>
              
              <p className="phone-validation-info">
                {selectedPaymentMethod === 'orange' 
                  ? 'Format: 6XX XXX XXX, 7XX XXX XXX ou 9XX XXX XXX'
                  : 'Format: 6XX XXX XXX, 7XX XXX XXX ou 9XX XXX XXX'
                }
              </p>
              
              {/* Afficher l'état du paiement Orange Money */}
              {selectedPaymentMethod === 'orange' && paymentStatus && (
                <div className={`payment-status ${paymentStatus.toLowerCase()}`}>
                  <Icon 
                    name={
                      paymentStatus === 'INITIATING' ? 'Clock' : 
                      paymentStatus === 'PENDING' ? 'Clock' : 
                      paymentStatus === 'SUCCESS' ? 'CheckCircle' : 
                      paymentStatus === 'FAILED' ? 'XCircle' : 
                      paymentStatus === 'TIMEOUT' ? 'Clock' : 'AlertCircle'
                    } 
                    size={16} 
                    color={
                      paymentStatus === 'SUCCESS' ? '#10b981' : 
                      paymentStatus === 'FAILED' || paymentStatus === 'TIMEOUT' ? '#ef4444' : 
                      '#f59e0b'
                    } 
                  />
                  <span className="payment-status-text">
                    {paymentMessage}
                  </span>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                className={`phone-submit-button ${(!phoneNumber || phoneNumber.length < 9 || paymentStatus === 'PENDING' || paymentStatus === 'SUCCESS') ? 'disabled' : ''}`}
                onClick={handlePayment}
                disabled={!phoneNumber || phoneNumber.length < 9 || paymentStatus === 'PENDING' || paymentStatus === 'SUCCESS' || paymentLoading}
              >
                {paymentLoading ? (
                  <div className="loader"></div>
                ) : paymentStatus === 'PENDING' || paymentStatus === 'SUCCESS' ? (
                  <span className="phone-submit-button-text">Paiement en cours...</span>
                ) : (
                  <>
                    <span className="phone-submit-button-text">Payer maintenant</span>
                    {selectedPack && (
                      <span className="phone-submit-button-amount">
                        {formatAlertPrice(selectedPack.price)}
                      </span>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
        
        {/* Modal de succès */}
        <Modal
          isOpen={showSuccessModal}
          onRequestClose={closeAllModals}
          style={customSuccessModalStyles}
          contentLabel="Paiement réussi"
        >
          <div className="modal-content-inner">
            <div className="success-icon-container">
              <Icon name="CheckCircle" size={80} color="#10b981" />
            </div>
            
            <h2 className="success-title">Paiement effectué avec succès !</h2>
            
            <p className="success-message">
              Votre alerte a été activée. Vous serez notifié dès qu'un produit correspondant à votre recherche sera disponible.
            </p>
            
            <div className="success-details">
              <div className="success-detail-row">
                <span className="success-detail-label">Pack:</span>
                <span className="success-detail-value">{selectedPack?.name}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Durée:</span>
                <span className="success-detail-value">{selectedPack?.duration}</span>
              </div>
              <div className="success-detail-row">
                <span className="success-detail-label">Montant:</span>
                <span className="success-detail-value">{selectedPack ? formatAlertPrice(selectedPack.price) : ''}</span>
              </div>
              {paymentData && (
                <div className="success-detail-row">
                  <span className="success-detail-label">Transaction:</span>
                  <span className="success-detail-value">{paymentData.payToken}</span>
                </div>
              )}
            </div>
            
            <button
              className="success-button"
              onClick={closeAllModals}
            >
              <span className="success-button-text">Fermer</span>
            </button>
          </div>
        </Modal>
        
        {/* Indicateur de chargement */}
        {loading && page === 1 && (
          <div className="loading-container">
            <div className="loader large"></div>
            <p className="loading-text">Recherche en cours...</p>
          </div>
        )}
      </div>
    );
  };
  
  export default Rechercher;